import Ellipsis from "@/components/views/Ellipsis";
import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { formatLocaleCurrency, formatLocaleDate } from "@/utils/formatUtils";
import { isDefined } from "@/utils/utils";
import { Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { PageSizes, TableSizes } from "../../../../../common/constants";
import { CommissionKindTag } from "../../../batch/components/CommissionKindTag";
import { CommissionBatchName } from "../../../components/CommissionBatchName";
import { CommissionAdvance, CommissionAdvanceInput } from "../../types";

interface Props {
  advance: CommissionAdvance;
}

export const CommissionAdvanceInputsTable = ({ advance }: Props) => {
  const { t } = useTranslation();

  const columns = useMemo<ColumnsType<CommissionAdvanceInput>>(
    () => [
      {
        key: "batch",
        title: t("commissions.advance.attrs.inputs.batch"),
        width: 180,
        ellipsis: { showTitle: false },
        render: (_, record) => <CommissionBatchName batchName={record.batch.name} batchId={record.batch.id} showLink />,
      },
      {
        key: "type",
        title: t("commissions.advance.enums.inputType._label"),
        width: 180,
        render: (_, record) => t("commissions.advance.enums.inputType." + record.type),
      },
      {
        key: "tariffCode",
        title: t("commissions.advance.attrs.inputs.tariffCode"),
        width: 100,
        ellipsis: { showTitle: false },
        render: (_, record) => <Ellipsis>{record.tariffCode}</Ellipsis>,
      },
      {
        key: "dates",
        title: t("commissions.batch.attrs.commission.dates"),
        width: 100,
        render: (_, record) => (
          <>
            {record.startDate ? formatLocaleDate(record.startDate) : undefined}
            <br />
            {record.endDate ? formatLocaleDate(record.endDate) : undefined}
          </>
        ),
      },
      {
        key: "commissionKind",
        title: t("commissions.batch.enums.commissionKind._label"),
        align: "center",
        width: 170,
        render: (_, record) => <CommissionKindTag kind={record.commissionKind} />,
      },
      {
        key: "commissionBaseAmount",
        title: t("commissions.advance.attrs.inputs.commissionBaseAmount"),
        align: "right",
        width: 130,
        render: (_, record) =>
          isDefined(record.commissionBaseAmount) ? formatLocaleCurrency(record.commissionBaseAmount) : undefined,
      },
      {
        key: "commissionAmount",
        title: t("commissions.advance.attrs.inputs.commissionAmount"),
        align: "right",
        width: 120,
        render: (_, record) => formatLocaleCurrency(record.commissionAmount),
      },
      {
        key: "cycleId",
        title: t("commissions.advance.attrs.inputs.cycleId"),
        width: 170,
        render: (_, record) => {
          const cycle = advance.cycles.find((cycle) => cycle.id === record.cycleId);

          return cycle ? (
            <CommissionBatchName batchName={cycle.batch.name} batchId={cycle.batch.id} showLink />
          ) : (
            <span className="sub-header-info">{t("commissions.advance.helpers.awaitingCycle")}</span>
          );
        },
      },
    ],
    [advance.cycles, t],
  );

  return (
    <Table<CommissionAdvanceInput>
      {...tableStandardProps()}
      columns={columns}
      dataSource={advance.inputs}
      scroll={{ x: TableSizes.LARGE }}
      pagination={{
        ...paginationTableProps,
        placement: ["bottomEnd"],
        pageSize: PageSizes.TINY,
        total: advance.inputs.length,
      }}
    />
  );
};
